import React, { useState } from 'react';
import {
  Box, CssBaseline, Typography, IconButton, Drawer,
  useMediaQuery, Avatar, Divider, Tooltip
} from '@mui/material';
import {
  House, HouseOutlined, PersonOutlined, Person,
  NotificationsOutlined, Notifications as NotificationsIcon,
  DevicesOther, DevicesOtherOutlined,
  PeopleAlt, PeopleAltOutlined,
  Group, GroupOutlined,
  SportsEsports, SportsEsportsOutlined,
  MonetizationOn, MonetizationOnOutlined,
  Inventory2, Inventory2Outlined,
  ShoppingBag, ShoppingBagOutlined,
  HomeWork, HomeWorkOutlined,
  Event, EventOutlined,
  Handshake, HandshakeOutlined,
  Memory, MemoryOutlined,
  EngineeringOutlined, Engineering,
  SupportAgentOutlined, SupportAgent,
  Menu as MenuIcon, Circle,
  ChevronRight, ChevronLeft,
  SystemUpdateAlt, SystemUpdateAltOutlined
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import Show from '../common/show';
import { Connect, mapStateToProps } from '../../Redux';
import { UserProps } from '../../types/user';
import logo from '../../assets/images/pool.jpeg';

type Screen = 'home' | 'notifications' | 'devices' | 'clients' | 'users' | 'games' | 'revenue' | 'products' | 'spaces' | 'orders' | 'gadgetOrders' | 'installers' | 'marketplaceStaff' | 'firmware' | 'events' | 'sponsors' | 'profile';

interface Props {
  unViewedNotifications?: boolean;
  User?: UserProps;
}

const SIDEBAR_WIDTH = 220;
const SIDEBAR_COLLAPSED_WIDTH = 64;

// Matches the gradient/palette introduced on the Sign In page (Auth/index.tsx) - kept as its
// own copy here since it's a presentation constant, not shared state.
const PRIMARY = '#006B46';
const PRIMARY_DARK = '#00432D';
const INK = '#0b2e20';
// Solid (non-transparent) tones for text on the gradient - opacity-blended grays read as
// unfinished against a colored background and shift contrast as the gradient darkens; a fixed
// ink color stays legible and crisp at every point on it.
const TEXT_DARK = '#123425';
const TEXT_MUTED = '#3f6b57';

// Ordered by importance. Owner-facing tabs (adminOnly absent) come first, in the order a
// table owner actually uses them day to day; admin-only platform-management tabs are
// interleaved after their closest related owner tab (Revenue/Clients right after the
// operational tabs they report on, Products/Orders grouped together, Events last).
const tabs: { label: string; path: Screen; icon: React.ReactNode; activeIcon: React.ReactNode; adminOnly?: boolean }[] = [
  { label: 'Home', path: 'home', icon: <HouseOutlined />, activeIcon: <House /> },
  { label: 'Devices', path: 'devices', icon: <DevicesOtherOutlined />, activeIcon: <DevicesOther /> },
  { label: 'Games', path: 'games', icon: <SportsEsportsOutlined />, activeIcon: <SportsEsports /> },
  { label: 'Revenue', path: 'revenue', icon: <MonetizationOnOutlined />, activeIcon: <MonetizationOn />, adminOnly: true },
  { label: 'Clients', path: 'clients', icon: <PeopleAltOutlined />, activeIcon: <PeopleAlt />, adminOnly: true },
  { label: 'All Users', path: 'users', icon: <GroupOutlined />, activeIcon: <Group />, adminOnly: true },
  { label: 'Products', path: 'products', icon: <Inventory2Outlined />, activeIcon: <Inventory2 />, adminOnly: true },
  { label: 'Spaces', path: 'spaces', icon: <HomeWorkOutlined />, activeIcon: <HomeWork />, adminOnly: true },
  { label: 'Orders', path: 'orders', icon: <ShoppingBagOutlined />, activeIcon: <ShoppingBag />, adminOnly: true },
  { label: 'Gadget Orders', path: 'gadgetOrders', icon: <MemoryOutlined />, activeIcon: <Memory />, adminOnly: true },
  { label: 'Installers', path: 'installers', icon: <EngineeringOutlined />, activeIcon: <Engineering />, adminOnly: true },
  { label: 'Marketplace Staff', path: 'marketplaceStaff', icon: <SupportAgentOutlined />, activeIcon: <SupportAgent />, adminOnly: true },
  { label: 'Firmware', path: 'firmware', icon: <SystemUpdateAltOutlined />, activeIcon: <SystemUpdateAlt />, adminOnly: true },
  { label: 'Events', path: 'events', icon: <EventOutlined />, activeIcon: <Event />, adminOnly: true },
  { label: 'Sponsors', path: 'sponsors', icon: <HandshakeOutlined />, activeIcon: <Handshake />, adminOnly: true },
  { label: 'Notifications', path: 'notifications', icon: <NotificationsOutlined />, activeIcon: <NotificationsIcon /> },
  { label: 'Profile', path: 'profile', icon: <PersonOutlined />, activeIcon: <Person /> },
];

const Screens: React.FC<Props> = ({ unViewedNotifications, User }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const isDesktop = useMediaQuery('(min-width:900px)');
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = User?.role === 'admin' || User?.role === 'Super Admin';
  const visibleTabs = tabs.filter(t => isAdmin || !t.adminOnly);
  const currentPath = (location.pathname.replace('/', '') || 'home') as Screen;
  const currentLabel = visibleTabs.find(t => t.path === currentPath)?.label ?? 'Home';
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const handleNavigate = (path: Screen) => {
    navigate(`/${path}`);
    setDrawerOpen(false);
  };

  const SidebarContent = ({ mobile = false }: { mobile?: boolean }) => (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      position: 'relative',
      // Runs from near-white mint through to a distinctly saturated green - a wider spread than
      // a first pass that stayed so pale the transition barely read as a gradient at all. Still
      // stops short of the deep PRIMARY green from the Sign In page: nav text is a solid ink
      // color (not translucent), which holds up well against this, but not against something
      // that dark.
      background: `linear-gradient(150deg, #f3fbf7 0%, #cdeddc 38%, #8fd3ab 72%, #4CAE84 100%)`,
      color: INK,
      overflow: 'hidden'
    }}>
      {/* Decorative blobs — echoes the Sign In page's branding panel */}
      <Box sx={{ position: 'absolute', top: -70, right: -70, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,.55) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: -70, left: -70, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${PRIMARY_DARK}35 0%, transparent 70%)`, pointerEvents: 'none' }} />

      {/* Logo */}
      <Box sx={{ position: 'relative', px: collapsed && !mobile ? 0 : 2.5, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: collapsed && !mobile ? 'center' : 'flex-start', gap: 1.5, minHeight: 64 }}>
        <Avatar src={logo} sx={{ width: 32, height: 32, flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }} />
        {(!collapsed || mobile) && (
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: PRIMARY_DARK, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
            SpotPool
          </Typography>
        )}
      </Box>

      <Divider sx={{ position: 'relative', borderColor: 'rgba(11,46,32,0.14)', mx: collapsed && !mobile ? 1 : 2, mb: 1 }} />

      {/* Nav Items */}
      <Box sx={{ position: 'relative', flex: 1, px: collapsed && !mobile ? 1 : 1.5, pt: 1, display: 'flex', flexDirection: 'column', gap: 0.5, overflowY: 'auto' }}>
        {visibleTabs.map(({ label, path, icon, activeIcon }) => {
          const active = currentPath === path;
          const item = (
            <Box
              key={path}
              onClick={() => handleNavigate(path)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed && !mobile ? 'center' : 'flex-start',
                gap: 1.5,
                px: collapsed && !mobile ? 0 : 2,
                py: 1.2,
                borderRadius: 2,
                cursor: 'pointer',
                position: 'relative',
                bgcolor: active ? PRIMARY : 'transparent',
                color: active ? '#fff' : TEXT_DARK,
                boxShadow: active ? '0 4px 12px rgba(0,107,70,0.35)' : 'none',
                transition: 'all 0.18s',
                '&:hover': {
                  bgcolor: active ? PRIMARY : 'rgba(255,255,255,0.5)',
                  color: active ? '#fff' : PRIMARY_DARK,
                },
              }}
            >
              <Box sx={{ display: 'flex', fontSize: active ? 21 : 20, position: 'relative', flexShrink: 0, opacity: active ? 1 : 0.85 }}>
                {active ? activeIcon : icon}
                {path === 'notifications' && unViewedNotifications && (
                  <Circle sx={{ position: 'absolute', top: -2, right: -2, height: 8, width: 8, color: '#ef4444' }} />
                )}
              </Box>
              {(!collapsed || mobile) && (
                <Typography sx={{
                  color: active ? '#fff' : TEXT_DARK,
                  fontSize: active ? '0.92rem' : '0.87rem',
                  fontWeight: active ? 600 : 500,
                  letterSpacing: active ? '0.02em' : '0.01em',
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}>
                  {label}
                </Typography>
              )}
            </Box>
          );
          return collapsed && !mobile ? (
            <Tooltip key={path} title={label} placement='right' arrow>
              {item}
            </Tooltip>
          ) : item;
        })}
      </Box>

      {/* Collapse toggle — desktop only */}
      {!mobile && (
        <Box sx={{ position: 'relative', px: 1, pb: 2 }}>
          <Divider sx={{ borderColor: 'rgba(11,46,32,0.14)', mb: 1.5 }} />
          <Box
            onClick={() => setCollapsed(c => !c)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 1,
              px: collapsed ? 0 : 1.5,
              py: 1,
              borderRadius: 2,
              cursor: 'pointer',
              color: TEXT_MUTED,
              '&:hover': { color: PRIMARY_DARK, bgcolor: 'rgba(255,255,255,0.5)' },
              transition: 'all 0.18s',
            }}
          >
            {collapsed ? <ChevronRight sx={{ fontSize: 20 }} /> : <ChevronLeft sx={{ fontSize: 20 }} />}
            {!collapsed && (
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                Collapse
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {(!collapsed || mobile) && (
        <Box sx={{ position: 'relative', px: 2.5, pb: 2 }}>
          <Typography sx={{ color: TEXT_MUTED, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.01em' }}>
            © {new Date().getFullYear()} SpotPool
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f9fafb' }}>
      <CssBaseline />

      {/* Desktop Sidebar */}
      {isDesktop && (
        <Box
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            zIndex: 100,
            boxShadow: '2px 0 12px rgba(0,0,0,0.12)',
            transition: 'width 0.22s ease',
            overflow: 'hidden',
          }}
        >
          <SidebarContent />
        </Box>
      )}

      {/* Mobile Drawer */}
      {!isDesktop && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: SIDEBAR_WIDTH, bgcolor: 'transparent' } }}
        >
          <SidebarContent mobile />
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          ml: isDesktop ? `${sidebarWidth}px` : 0,
          transition: 'margin-left 0.22s ease',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* Mobile Top Bar */}
        {!isDesktop && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 99,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.2,
              background: `linear-gradient(100deg, #f3fbf7 0%, #cdeddc 100%)`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: TEXT_DARK, p: 0.5 }}>
              <MenuIcon />
            </IconButton>
            <Avatar src={logo} sx={{ width: 28, height: 28 }} />
            <Typography sx={{ fontWeight: 700, color: TEXT_DARK, fontSize: '0.95rem', letterSpacing: '0.01em' }}>
              {currentLabel}
            </Typography>
          </Box>
        )}

        {/* Page Content — rendered by nested route */}
        <Box sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Connect(mapStateToProps)(Screens);
